import { BigNumber } from 'bignumber.js'

const balanceSuffixes = {
  trillions: 'T',
  billions: 'B',
  millions: 'M',
  thousands: 'K',
}

export type ValueWithSuffixParts = {
  value: number
  fractionDigits: number
  suffix: string
}

export const getValueWithSuffixParts = (
  value: number | string | undefined,
  addSuffix = true,
): ValueWithSuffixParts => {
  const n = Number.isNaN(Number(value)) ? 0 : Number(value)

  if (n === 0) {
    return { value: 0, fractionDigits: 3, suffix: '' }
  }
  if (n < 0.1) return { value: n, fractionDigits: 4, suffix: '' }
  if (n < 1e3) return { value: n, fractionDigits: 2, suffix: '' }
  if (n >= 1e3 && n < 1e6)
    return { value: n / 1e3, fractionDigits: 1, suffix: addSuffix ? balanceSuffixes.thousands : '' }
  if (n >= 1e6 && n < 1e9)
    return { value: n / 1e6, fractionDigits: 1, suffix: addSuffix ? balanceSuffixes.millions : '' }
  if (n >= 1e9 && n < 1e12)
    return { value: n / 1e9, fractionDigits: 1, suffix: addSuffix ? balanceSuffixes.billions : '' }
  if (n >= 1e12)
    return {
      value: n / 1e12,
      fractionDigits: 1,
      suffix: addSuffix ? balanceSuffixes.trillions : '',
    }

  return { value: 0, fractionDigits: 3, suffix: '' }
}

export function countDecimalPlaces(number: number | string) {
  const num = Number(number)
  if (Number.isNaN(num)) return 0
  if (Number.isInteger(num)) return 0
  let e = 1
  let p = 0
  while (Math.round(num * e) / e !== num) {
    e *= 10
    p++
  }
  return p
}

export const parseSafeIntegerInput = (value: string): number | null => {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return null

  let parsed: bigint
  try {
    parsed = BigInt(trimmed)
  } catch {
    return null
  }

  const maxSafeInteger = BigInt(Number.MAX_SAFE_INTEGER)
  if (parsed > maxSafeInteger) return null

  return Number(parsed)
}

export const parseAmount = (value?: string | number | null): number => {
  if (value === undefined || value === null) return 0
  const numeric = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(numeric) ? numeric : 0
}

export const parseFiniteNumber = (value?: string | number | null): number | null => {
  if (value === undefined || value === null) return null
  if (typeof value === 'string' && value.trim().length === 0) return null

  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const normalizeDecimalInput = (value: string | number): string | number => {
  if (typeof value !== 'string') return value

  const trimmedValue = value.trim()
  if (!trimmedValue) return 0

  return trimmedValue.includes(',') && !trimmedValue.includes('.')
    ? trimmedValue.replace(',', '.')
    : trimmedValue
}

export const parseDecimalBigNumber = (value: string | number): BigNumber => {
  try {
    const parsedValue = new BigNumber(normalizeDecimalInput(value))
    return parsedValue.isFinite() ? parsedValue : new BigNumber(0)
  } catch {
    return new BigNumber(0)
  }
}

export const floorBigNumberToDecimals = (value: BigNumber, decimals: number): number => {
  return value.decimalPlaces(decimals, BigNumber.ROUND_FLOOR).toNumber()
}

export type AmountRoundingMode = 'round' | 'truncate' | 'floor' | 'ceil'

const getBigNumberRoundingMode = (roundingMode: AmountRoundingMode) => {
  switch (roundingMode) {
    case 'truncate':
      return BigNumber.ROUND_DOWN
    case 'floor':
      return BigNumber.ROUND_FLOOR
    case 'ceil':
      return BigNumber.ROUND_CEIL
    case 'round':
    default:
      return BigNumber.ROUND_HALF_UP
  }
}

const resolveNumberLocale = (locale?: string) => {
  if (locale) return locale
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language
  return new Intl.NumberFormat().resolvedOptions().locale
}

export const roundAmountToDecimals = (
  value: string | number | undefined,
  decimals = 2,
  roundingMode: AmountRoundingMode = 'truncate',
): number => {
  const rounded = parseDecimalBigNumber(value ?? 0).decimalPlaces(
    Math.max(0, decimals),
    getBigNumberRoundingMode(roundingMode),
  )
  return rounded.toNumber()
}

type FormatAmountOptions = {
  locale?: string
  currency?: string
  showSymbol?: boolean
  minimumDecimals?: number
  roundingMode?: AmountRoundingMode
  useGrouping?: boolean
}

const getAmountFormatter = (
  decimals: number,
  {
    locale,
    currency = 'USD',
    showSymbol = false,
    minimumDecimals,
    useGrouping = true,
  }: FormatAmountOptions = {},
) =>
  new Intl.NumberFormat(resolveNumberLocale(locale), {
    style: showSymbol ? 'currency' : 'decimal',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: Math.min(minimumDecimals ?? decimals, decimals),
    maximumFractionDigits: decimals,
    useGrouping,
  })

export const formatTradeAmount = (
  value: string | number | undefined,
  decimals = 2,
  options: FormatAmountOptions = {},
): string => {
  const roundedValue = roundAmountToDecimals(value, decimals, options.roundingMode ?? 'round')
  return getAmountFormatter(decimals, options).format(roundedValue).trim()
}

export const formatTradeAmountToParts = (
  value: string | number | undefined,
  decimals = 2,
  options: FormatAmountOptions = {},
): Intl.NumberFormatPart[] => {
  const roundedValue = roundAmountToDecimals(value, decimals, options.roundingMode ?? 'round')
  return getAmountFormatter(decimals, options).formatToParts(roundedValue)
}

/**
 * Adjusts the price of an order to the nearest tick size.
 *
 * @param {number} price - The price that needs to be adjusted.
 * @param {number} tickSize - The tick size to which the price needs to be adjusted.
 * @returns {number} - The adjusted price.
 *
 * @example
 * // Case where the absolute difference between price and nearestMultiple is greater than or equal to tolerance
 * // Assuming we have an order with a price of 1003 and a tick size of 0.002
 * let order = { price: 1003 };
 * let tickSize = 0.002;
 * let adjustedPrice = adjustPriceToTickSize(order, tickSize);
 * Logger.log(adjustedPrice); // Outputs: 1002
 *
 * // Case where the absolute difference between price and nearestMultiple is less than tolerance
 * // Assuming we have an order with a price of 1002 and a tick size of 0.002
 * order = { price: 1002 };
 * tickSize = 0.002;
 * adjustedPrice = adjustPriceToTickSize(order, tickSize);
 * Logger.log(adjustedPrice); // Outputs: 1002
 */
export function adjustPriceToTickSize(price: number, tickSize: number): number {
  const tolerance = 0.000001 // tolerance threshold
  const tickDecimals = countDecimalPlaces(tickSize)
  const tickNumber = tickSize * 10 ** tickDecimals
  const nearestMultiple = Math.round(price / tickNumber) * tickNumber
  const remainder = Math.abs(nearestMultiple - price)

  if (Math.abs(price - nearestMultiple) >= tolerance) {
    return price - remainder
  }

  return price
}

export const getUSDValue = (value = 0): number => {
  if (Math.abs(value) < 1.0e-2) return 0
  return value
}

type FormatValueWithSuffixOptions = {
  locale?: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  prefix?: string
  useGrouping?: boolean
}

export const formatValueWithSuffix = (
  value: number | string | undefined,
  addSuffix = true,
  {
    locale,
    minimumFractionDigits,
    maximumFractionDigits,
    prefix = '',
    useGrouping = true,
  }: FormatValueWithSuffixOptions = {},
): string => {
  const { value: scaledValue, fractionDigits, suffix } = getValueWithSuffixParts(value, addSuffix)
  const maximumDigits = Math.max(0, maximumFractionDigits ?? fractionDigits)
  const minimumDigits = Math.min(Math.max(0, minimumFractionDigits ?? maximumDigits), maximumDigits)

  const formattedValue = new Intl.NumberFormat(resolveNumberLocale(locale), {
    minimumFractionDigits: minimumDigits,
    maximumFractionDigits: maximumDigits,
    useGrouping,
  }).format(scaledValue)

  return `${prefix}${formattedValue}${suffix}`
}

type FormatCompactCurrencyOptions = {
  locale?: string
  currency?: string
  compactThreshold?: number
  standardFractionDigits?: number
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

export const formatCompactCurrency = (
  value: number | string | undefined,
  {
    locale = 'en-US',
    currency = 'USD',
    compactThreshold = 1_000,
    standardFractionDigits = 2,
    minimumFractionDigits = 1,
    maximumFractionDigits = 2,
  }: FormatCompactCurrencyOptions = {},
): string => {
  const numericValue = Number(value)
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0
  const standardDigits = Math.max(0, standardFractionDigits)
  const shouldCompact = Math.abs(safeValue) >= compactThreshold

  if (shouldCompact) {
    return formatValueWithSuffix(safeValue, true, {
      locale,
      minimumFractionDigits,
      maximumFractionDigits,
      prefix: currency === 'USD' ? '$' : `${currency} `,
    })
  }

  const formattedValue = new Intl.NumberFormat(resolveNumberLocale(locale), {
    minimumFractionDigits: standardDigits,
    maximumFractionDigits: standardDigits,
  }).format(safeValue)

  return `${currency === 'USD' ? '$' : `${currency} `}${formattedValue}`.trim()
}

/**
 * Formats a number as currency using the specified locale and adds the currency symbol.
 * @param number - The number to format.
 * @param decimals - The number of decimal places to show in the formatted number.
 * @param locale - The locale to use for formatting (e.g., 'en-US', 'de-DE', etc.).
 * @param currency - The ISO currency code (e.g., 'USD', 'EUR', 'JPY', etc.).
 * @param showSymbol - A boolean value that determines whether to show the currency symbol or not.
 * @param roundingMode - How to handle decimal rounding.
 * @returns The formatted number as a string with the currency symbol.
 */
export function formatNumber(
  number: number | string | undefined,
  decimals?: number | undefined,
  locale?: string,
  currency?: string,
  showSymbol?: boolean,
  roundingMode: 'round' | 'truncate' = 'round',
): string {
  locale = locale || new Intl.NumberFormat().resolvedOptions().locale || navigator.language
  currency = currency || 'USD'
  decimals = decimals !== undefined ? decimals : 2

  const formattedNumber = Number.isNaN(Number(number)) ? 0 : Number(number)
  const adjustedNumber =
    roundingMode === 'truncate'
      ? Math.trunc(formattedNumber * 10 ** decimals) / 10 ** decimals
      : formattedNumber

  showSymbol = showSymbol !== undefined ? showSymbol : false

  return new Intl.NumberFormat(locale, {
    style: showSymbol ? 'currency' : 'decimal',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
    .format(adjustedNumber)
    .trim()
}

export const formatSignedPercent = (value: number, decimals = 2): string => {
  const sign = value > 0 ? '+' : ''
  return `${sign}${formatNumber(value, decimals)}%`
}

/**
 * Converts a formatted number string back to a number.
 * @param numberString - The formatted number string to convert.
 * @returns The number.
 */
export function removeFormatNumber(numberString: string): string {
  // Remove any non-numeric characters except the decimal point
  const sanitizedString = numberString.replace(/[^0-9.]/g, '')

  // Convert the sanitized string to a number
  return (Number.isNaN(sanitizedString) ? 0 : Number(sanitizedString)).toString()
}
